import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, map } from 'rxjs';
import * as objectHash from 'object-hash';

@Injectable({
    providedIn: 'root',
})
/**
 * A utility class to detect unsaved changes in an object of type `T`.
 * 
 * This class uses RxJS to observe changes to an object and determine if there are any unsaved changes
 * by comparing the hash values of the initial and current states of the object.
 * 
 * @template T - The type of the object being observed for changes.
 */
export class DetectUnsavedChanges<T> {
    private subject = new BehaviorSubject<T | undefined>(undefined);

    public observable = this.subject.asObservable();

    private loadedHash: string | undefined = undefined;
    private currentHash: string | undefined = undefined;

    private unsavedChangesExist: boolean = false;

    constructor() {
        this.observable.subscribe((object) => {
            const objectHashValue = objectHash(object ?? {});
            
            this.loadedHash === undefined 
                ? this.loadedHash = objectHashValue 
                : this.currentHash = objectHashValue;

            this.unsavedChangesExist =
                this.currentHash !== undefined &&
                this.loadedHash !== undefined &&
                this.currentHash !== this.loadedHash;
        });
    }

    public setObject(object: T | undefined): void {
        this.subject.next(object);
    }

    public markAsSaved(): void {
        if (this.currentHash) {
            this.loadedHash = this.currentHash; // Reset to the saved state
        }
        this.unsavedChangesExist = false;
    }

    public hasUnsavedChanges(): boolean {
        return this.unsavedChangesExist;
    }

    public getCurrentValue(): T | undefined {
        return this.subject.value as T;
    }
}
